import update from 'immutability-helper';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { cancelEnrolment } from '../../../../actions/hub/hubCancelActions';
import { api } from '../../../../api';
import type { Enrolment } from '../../../../api/types/enrolment';
import ErrorBlock from '../../../../common/ErrorBlock';
import ContactFormLayout from '../../../../common/hub/ContactFormLayout';
import useAccountPricingPlanQuery from '../../../../hooks/queries/useAccountPricingPlanQuery';
import { CancelReason } from '../../../../types/hubInterfaces';
import { InitialState, UserPricingPlan } from '../../../../types/hubTypes';
import SummerPauseAndDiscount from '../../../../v2/features/cancel-enrolment/SummerPauseAndDiscount';
import { isSummerCancelPromoEnabled } from '../../../../v2/features/pause-enrolment/helpers';
import ChangePricingPlanPage from '../../../../views/account/ChangePricingPlanPage';
import { DatePicker, useDatePickerProps } from '../../../DatePicker/DatePicker';
import FormSubHeading from '../../../hub/contact/FormSubHeading';
import { CancellationDeterrent } from '../CancellationDeterrent';
import { CancellationSteps } from '../CancellationSteps';
import { CancelEnrolmentAlert } from './CancelEnrolmentAlert';

interface Props {
  selectedEnrolment: Enrolment;
  goBack(): void;
  submitSuccessCallback(): void;
  enrolmentPolicyError?: unknown;
}

const changePlanWhitelist = [
  'Flexi 23',
  'Flexi 2306',
  'Flexi',
  'Flexi 2312',
  'Saver 24',
  'Saver 2408',
  'Saver 2506',
  'Saver',
  'Saver 2501',
  'Saver Plus 2507',
  'Saver 2507',
];

function shouldSuggestChangePlan(
  selectedReasons: CancelReason[],
  currentPricingPlan: UserPricingPlan,
  upcomingPricingPlan?: UserPricingPlan
) {
  const isChangingToValueMax = upcomingPricingPlan?.pricingPlanDisplayName === 'Value Max';
  return (
    !!currentPricingPlan &&
    !isChangingToValueMax &&
    changePlanWhitelist.includes(currentPricingPlan.pricingPlanName) &&
    selectedReasons.some(
      (reason) =>
        reason.label === "The price doesn't work for us" || reason.label === 'We can no longer afford the service'
    )
  );
}

const shouldOfferSummerPauseAndDiscount = (selectedReasons: CancelReason[]) => {
  return (
    isSummerCancelPromoEnabled() && selectedReasons.some((reason) => reason.label === 'Cancelling for summer holiday')
  );
};

const FormCancelEnrolment = ({ selectedEnrolment, goBack, submitSuccessCallback, enrolmentPolicyError }: Props) => {
  const { enrolmentSfid } = selectedEnrolment;
  const dispatch = useDispatch();
  const ref = useRef(null);
  const previousRatingRef = useRef(0);
  const { fetchError: submitError, errorMsg } = useSelector((state: InitialState) => state.ui.apiState.hubContactPage);
  const { data: accountPricingPlan } = useAccountPricingPlanQuery();
  const { data: salesforceCases, error: salesforceCasesQueryError } = api.salesforceCase.all.useQuery(enrolmentSfid);
  const hasTakenSummerOffer = salesforceCases?.cases?.length > 0;

  const datePickerProps = useDatePickerProps({
    labelText: 'Select a date to cancel from',
    placeholder: 'Select start date',
    customInputClasses: 'w-full',
    options: {
      enableTime: false,
      minDate: new Date().fp_incr(1),
      altInput: true,
      altFormat: 'F j, Y',
      dateFormat: 'Y-m-d',
    },
  });
  const selectedDate =
    Array.isArray(datePickerProps.value) && datePickerProps.value[0] instanceof Date === true
      ? moment(datePickerProps.value[0]).format('YYYY-MM-DD')
      : '';

  const [cancelSelection, setCancelSelection] = useState({
    selectedDate,
    rating: 0,
    selectedReasons: [],
    message: '',
    other: {
      message: '',
    },
  });

  useEffect(() => {
    if (
      (cancelSelection.rating <= 3 && previousRatingRef.current >= 4) ||
      (cancelSelection.rating >= 4 && previousRatingRef.current <= 3)
    ) {
      setCancelSelection((current) =>
        update(current, {
          selectedReasons: {
            $set: [],
          },
        })
      );
    }
  }, [cancelSelection.rating]);
  useEffect(() => {
    previousRatingRef.current = cancelSelection.rating;
  }, [cancelSelection.rating]);
  useEffect(() => {
    setCancelSelection((current) =>
      update(current, {
        selectedDate: {
          $set: selectedDate,
        },
      })
    );
  }, [selectedDate]);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showSummerPauseAndDiscount, setShowSummerPauseAndDiscount] = useState(false);
  const [cancelEnrolmentPayload, setCancelEnrolmentPayload] = useState(null);
  const handleCancelEnrolment = (onSubmitSuccess: () => void, onSubmitFailed: () => void) => {
    dispatch(
      cancelEnrolment({
        rating: cancelSelection.rating,
        enrolmentId: enrolmentSfid,
        selectedReasons: cancelSelection.selectedReasons,
        otherMessage: cancelSelection.other.message,
        message: cancelSelection.message,
        endDate: selectedDate,
        onSubmitSuccess,
        onSubmitFailed,
      })
    );
  };

  // Change here
  const onClickHandler = () => {
    if (shouldOfferSummerPauseAndDiscount(cancelSelection.selectedReasons) && !hasTakenSummerOffer) {
      setCancelEnrolmentPayload({
        rating: cancelSelection.rating,
        enrolmentId: enrolmentSfid,
        selectedReasons: cancelSelection.selectedReasons,
        otherMessage: cancelSelection.other.message,
        message: cancelSelection.message,
        endDate: selectedDate,
        onSubmitSuccess: submitSuccessCallback,
      });
      setShowSummerPauseAndDiscount(true);
    } else if (
      shouldSuggestChangePlan(
        cancelSelection.selectedReasons,
        accountPricingPlan?.currentPricingPlan,
        accountPricingPlan?.upcomingPricingPlan
      )
    ) {
      setCancelEnrolmentPayload({
        rating: cancelSelection.rating,
        enrolmentId: enrolmentSfid,
        selectedReasons: cancelSelection.selectedReasons,
        otherMessage: cancelSelection.other.message,
        message: cancelSelection.message,
        endDate: selectedDate,
        onSubmitSuccess: submitSuccessCallback,
      });
      setShowChangePlan(true);
    } else {
      dispatch(
        cancelEnrolment({
          rating: cancelSelection.rating,
          enrolmentId: enrolmentSfid,
          selectedReasons: cancelSelection.selectedReasons,
          otherMessage: cancelSelection.other.message,
          message: cancelSelection.message,
          endDate: selectedDate,
          onSubmitSuccess: submitSuccessCallback,
        })
      );
    }
  };

  const onGoBack = () => {
    if (ref?.current?.getStepIndex() > 0) {
      ref.current.goBack();
      window.scrollTo(0, document.body.scrollHeight);
    } else {
      goBack();
    }
  };

  if (showChangePlan) {
    return (
      <ChangePricingPlanPage
        interrupting
        cancelEnrolmentPayload={cancelEnrolmentPayload}
        onGoBack={() => setShowChangePlan(false)}
      />
    );
  }

  if (showSummerPauseAndDiscount) {
    return (
      <SummerPauseAndDiscount selectedEnrolment={selectedEnrolment} handleCancelEnrolment={handleCancelEnrolment} />
    );
  }

  return (
    <ContactFormLayout selectedEnrolment={selectedEnrolment} customGoBack={onGoBack}>
      <div className="px-5 pt-5 lg:px-20 lg:pt-14">
        {submitError && errorMsg && <ErrorBlock errorMsg={errorMsg} />}
        {enrolmentPolicyError && (
          <ErrorBlock errorMsg="There was an error encountered while retrieving the pause policy." />
        )}
        {salesforceCasesQueryError && (
          <ErrorBlock errorMsg="There was an error encountered while retrieving some information." />
        )}
        {ref?.current?.getStepIndex() <= 0 && <CancellationDeterrent />}
        <FormSubHeading />
        <p className="mb-5 text-grey-5">
          We're sorry to see you go. We need some information from you to process your request.
        </p>
        <form>
          <div className="mb-3 max-w-md">
            <DatePicker {...datePickerProps} />
          </div>
          <CancelEnrolmentAlert selectedDate={selectedDate} selectedEnrolment={selectedEnrolment} />
          <CancellationSteps
            ref={ref}
            onSubmitCancelRequest={onClickHandler}
            cancelSelection={cancelSelection}
            setCancelSelection={setCancelSelection}
            selectedEnrolment={selectedEnrolment}
          />
        </form>
      </div>
    </ContactFormLayout>
  );
};

export default FormCancelEnrolment;
